import React, { useEffect, useMemo, useState } from 'react';
import {
  X, Loader2, AlertTriangle, CheckCircle2, Undo2, ArrowLeftRight,
  FileText, BarChart3, Users, ShieldAlert,
} from 'lucide-react';
import {
  useAnalyserCompletudeMutation,
  useReporterDevoirManquantMutation,
  useReporterExamenVersDevoirsTraceMutation,
  useAnnulerBatchMutation,
} from '../../services/notesDevoirsApi';
import type {
  AnalyseCompletudeResponse,
  EtudiantManquant,
  ReportBatchResponse,
} from '../../services/notesDevoirsApi';

interface Props {
  open: boolean;
  onClose: () => void;
  matiereId: number;
  matiereNom: string;
  matiereCode: string;
  classes: string[];
  anneeAcademique: string;
  semestre: string;
  /** Si fourni, restreint la pré-sélection à ces étudiants (ex: vue étudiant unique). */
  selectionInitiale?: number[];
}

type TabKey = 'analyse' | 'devoir_manquant' | 'examen_vers_devoir';

const ReportIntelligentModal: React.FC<Props> = ({
  open, onClose, matiereId, matiereNom, matiereCode, classes, anneeAcademique, semestre,
  selectionInitiale,
}) => {
  const [tab, setTab] = useState<TabKey>('analyse');
  const [selection, setSelection] = useState<Set<number>>(new Set());
  const [lastBatch, setLastBatch] = useState<ReportBatchResponse | null>(null);
  const [confirmAction, setConfirmAction] = useState<null | 'devoir' | 'examen'>(null);

  const [analyser, { data: analyse, isLoading: analysing, reset: resetAnalyse }] = useAnalyserCompletudeMutation();
  const [reporterDevoir, { isLoading: reportingDevoir }] = useReporterDevoirManquantMutation();
  const [reporterExamen, { isLoading: reportingExamen }] = useReporterExamenVersDevoirsTraceMutation();
  const [annuler, { isLoading: annulant }] = useAnnulerBatchMutation();

  // Lancer l'analyse à l'ouverture
  useEffect(() => {
    if (open && matiereId && classes.length > 0) {
      setLastBatch(null);
      setSelection(new Set());
      setTab('analyse');
      analyser({
        matiere_id: matiereId,
        annee_academique: anneeAcademique,
        semestre,
        classes,
      });
    }
    if (!open) {
      resetAnalyse();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, matiereId, anneeAcademique, semestre, classes.join(',')]);

  // Liste d'étudiants pour l'onglet actif
  const candidats: EtudiantManquant[] = useMemo(() => {
    if (!analyse) return [];
    if (tab === 'devoir_manquant') {
      // Étudiants avec UN SEUL devoir saisi (D1 ou D2 mais pas les deux)
      const ids = new Set<number>();
      const list: EtudiantManquant[] = [];
      [...analyse.manque_d1, ...analyse.manque_d2].forEach((e) => {
        if (!ids.has(e.etudiant_id)) {
          ids.add(e.etudiant_id);
          list.push(e);
        }
      });
      return list;
    }
    if (tab === 'examen_vers_devoir') {
      // Étudiants sans aucune note de devoir mais avec une note d'examen
      return analyse.manque_les_deux.filter((e) => e.note_examen !== null || e.absent_examen);
    }
    return [];
  }, [analyse, tab]);

  // Sélection auto au changement d'onglet : tout sélectionner par défaut,
  // sauf si `selectionInitiale` est fourni (mode "étudiant unique") → on restreint.
  useEffect(() => {
    if (selectionInitiale && selectionInitiale.length > 0) {
      const allowed = new Set(selectionInitiale);
      setSelection(new Set(candidats.filter((e) => allowed.has(e.etudiant_id)).map((e) => e.etudiant_id)));
    } else {
      setSelection(new Set(candidats.map((e) => e.etudiant_id)));
    }
  }, [candidats, selectionInitiale]);

  const toggleEtud = (id: number) => {
    setSelection((s) => {
      const next = new Set(s);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleAll = () => {
    if (selection.size === candidats.length) setSelection(new Set());
    else setSelection(new Set(candidats.map((e) => e.etudiant_id)));
  };

  const doReportDevoir = async () => {
    setConfirmAction(null);
    try {
      const res = await reporterDevoir({
        matiere_id: matiereId,
        annee_academique: anneeAcademique,
        semestre,
        etudiants_ids: Array.from(selection),
      }).unwrap();
      setLastBatch(res);
      // Relancer l'analyse pour rafraîchir
      analyser({ matiere_id: matiereId, annee_academique: anneeAcademique, semestre, classes });
    } catch (e: any) {
      alert(e?.data?.error || 'Erreur lors du report');
    }
  };

  const doReportExamen = async () => {
    setConfirmAction(null);
    try {
      const res = await reporterExamen({
        matiere_id: matiereId,
        annee_academique: anneeAcademique,
        semestre,
        etudiants_ids: Array.from(selection),
        mode: 'manquants_seulement',
      }).unwrap();
      setLastBatch(res);
      analyser({ matiere_id: matiereId, annee_academique: anneeAcademique, semestre, classes });
    } catch (e: any) {
      alert(e?.data?.error || 'Erreur lors du report');
    }
  };

  const doAnnuler = async () => {
    if (!lastBatch) return;
    if (!window.confirm('Annuler cette opération ? Les notes reviendront à leur valeur précédente.')) return;
    try {
      const res = await annuler({ batch_id: lastBatch.batch_id }).unwrap();
      alert(`${res.nb_modifications_restaurees} modification(s) restaurée(s) sur ${res.nb_notes_devoirs_affectees} étudiant(s).`);
      setLastBatch(null);
      analyser({ matiere_id: matiereId, annee_academique: anneeAcademique, semestre, classes });
    } catch (e: any) {
      alert(e?.data?.error || 'Erreur lors de l\'annulation');
    }
  };

  if (!open) return null;

  const stats = analyse?.stats;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4 overflow-y-auto">
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl w-full max-w-5xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200 dark:border-gray-700">
          <div>
            <div className="flex items-center gap-2">
              <BarChart3 size={20} className="text-blue-600 dark:text-blue-400" />
              <h2 className="text-lg font-bold text-gray-900 dark:text-gray-100">
                Report intelligent
              </h2>
            </div>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
              <span className="font-mono">{matiereCode}</span> — {matiereNom} · Classes : {classes.join(', ')} · {semestre}
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700"
          >
            <X size={18} />
          </button>
        </div>

        {/* Stats globales */}
        <div className="px-5 py-4 bg-gray-50 dark:bg-gray-700/30 border-b border-gray-200 dark:border-gray-700">
          {analysing ? (
            <div className="flex items-center justify-center py-6 text-sm text-gray-500">
              <Loader2 size={16} className="animate-spin mr-2" />
              Analyse en cours…
            </div>
          ) : analyse ? (
            <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
              <StatCard label="Effectif" value={String(analyse.total_etudiants)} icon={<Users size={14} />} color="blue" />
              <ProgressCard label="D1 saisis" pct={stats?.pct_d1 ?? 0} count={`${stats?.nb_d1}/${analyse.total_etudiants}`} />
              <ProgressCard label="D2 saisis" pct={stats?.pct_d2 ?? 0} count={`${stats?.nb_d2}/${analyse.total_etudiants}`} />
              <ProgressCard label="Examens saisis" pct={stats?.pct_examen ?? 0} count={`${stats?.nb_examen}/${analyse.total_etudiants}`} />
            </div>
          ) : null}

          {analyse && (
            <div className="mt-3 grid grid-cols-1 md:grid-cols-3 gap-2 text-xs">
              <Hint
                level={analyse.manque_d1.length + analyse.manque_d2.length > 0 ? 'warn' : 'ok'}
                text={`${analyse.manque_d1.length + analyse.manque_d2.length} étudiant(s) avec UN SEUL devoir saisi`}
              />
              <Hint
                level={analyse.manque_les_deux.length > 0 ? 'warn' : 'ok'}
                text={`${analyse.manque_les_deux.length} étudiant(s) sans aucun devoir`}
              />
              <Hint
                level={analyse.manque_examen.length > 0 ? 'warn' : 'ok'}
                text={`${analyse.manque_examen.length} étudiant(s) sans note d'examen`}
              />
            </div>
          )}
        </div>

        {/* Bandeau résultat dernier batch */}
        {lastBatch && (
          <div className="mx-5 mt-3 p-3 rounded-lg bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-700/50 flex items-start justify-between gap-2">
            <div className="flex items-start gap-2 flex-1">
              <CheckCircle2 size={16} className="text-green-600 dark:text-green-400 mt-0.5" />
              <div className="text-xs text-green-800 dark:text-green-300">
                <div className="font-semibold">Opération réussie</div>
                <div>
                  {lastBatch.type_operation === 'report_devoir_manquant'
                    ? `${lastBatch.nb_reportes ?? 0} note(s) reportée(s)`
                    : `${(lastBatch.nb_crees ?? 0) + (lastBatch.nb_modifies ?? 0)} note(s) traitée(s)`} ·
                  {lastBatch.nb_ignores} ignoré(s)
                </div>
                <div className="font-mono text-[10px] opacity-70 mt-0.5">batch: {lastBatch.batch_id.slice(0, 8)}…</div>
              </div>
            </div>
            <button
              onClick={doAnnuler}
              disabled={annulant}
              className="shrink-0 inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium rounded border border-orange-300 text-orange-700 hover:bg-orange-50 dark:border-orange-700/50 dark:text-orange-300 dark:hover:bg-orange-900/20 disabled:opacity-50"
            >
              {annulant ? <Loader2 size={12} className="animate-spin" /> : <Undo2 size={12} />}
              Annuler ce report
            </button>
          </div>
        )}

        {/* Onglets actions */}
        <div className="px-5 pt-3 border-b border-gray-200 dark:border-gray-700">
          <div className="flex gap-1">
            <TabBtn active={tab === 'analyse'} onClick={() => setTab('analyse')} icon={<BarChart3 size={14} />}>
              Vue d'ensemble
            </TabBtn>
            <TabBtn active={tab === 'devoir_manquant'} onClick={() => setTab('devoir_manquant')} icon={<ArrowLeftRight size={14} />}>
              Report D1↔D2 ({(analyse?.manque_d1.length ?? 0) + (analyse?.manque_d2.length ?? 0)})
            </TabBtn>
            <TabBtn active={tab === 'examen_vers_devoir'} onClick={() => setTab('examen_vers_devoir')} icon={<FileText size={14} />}>
              Examen → D1+D2 ({analyse?.manque_les_deux.length ?? 0})
            </TabBtn>
          </div>
        </div>

        {/* Contenu */}
        <div className="flex-1 overflow-y-auto px-5 py-4">
          {tab === 'analyse' && analyse && (
            <AnalyseView analyse={analyse} />
          )}

          {(tab === 'devoir_manquant' || tab === 'examen_vers_devoir') && analyse && (
            <CandidatsList
              etudiants={candidats}
              selection={selection}
              onToggle={toggleEtud}
              onToggleAll={toggleAll}
              tab={tab}
            />
          )}
        </div>

        {/* Footer actions */}
        <div className="flex items-center justify-between px-5 py-3 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-700/30 rounded-b-xl">
          <div className="text-xs text-gray-500 dark:text-gray-400">
            {tab !== 'analyse' && `${selection.size} sélectionné(s) sur ${candidats.length}`}
          </div>
          <div className="flex gap-2">
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
            >
              Fermer
            </button>
            {tab === 'devoir_manquant' && (
              <button
                onClick={() => setConfirmAction('devoir')}
                disabled={selection.size === 0 || reportingDevoir}
                className="px-4 py-2 text-sm bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white rounded-lg flex items-center gap-2"
              >
                {reportingDevoir ? <Loader2 size={14} className="animate-spin" /> : <ArrowLeftRight size={14} />}
                Reporter D1↔D2 ({selection.size})
              </button>
            )}
            {tab === 'examen_vers_devoir' && (
              <button
                onClick={() => setConfirmAction('examen')}
                disabled={selection.size === 0 || reportingExamen}
                className="px-4 py-2 text-sm bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white rounded-lg flex items-center gap-2"
              >
                {reportingExamen ? <Loader2 size={14} className="animate-spin" /> : <FileText size={14} />}
                Reporter Examen → D1+D2 ({selection.size})
              </button>
            )}
          </div>
        </div>

        {/* Confirmation */}
        {confirmAction && (
          <div className="absolute inset-0 bg-black/50 rounded-xl flex items-center justify-center p-4">
            <div className="bg-white dark:bg-gray-800 rounded-lg p-5 max-w-md w-full shadow-2xl border border-gray-200 dark:border-gray-700">
              <div className="flex items-start gap-3 mb-4">
                <ShieldAlert size={24} className="text-amber-500 shrink-0" />
                <div>
                  <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100">
                    Confirmer le report
                  </h3>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                    {confirmAction === 'devoir'
                      ? `Reporter la note présente sur la note manquante pour ${selection.size} étudiant(s) ?`
                      : `Reporter la note d'examen sur D1 et D2 pour ${selection.size} étudiant(s) ?`}
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                    Cette action est tracée dans l'historique et peut être annulée.
                  </p>
                </div>
              </div>
              <div className="flex justify-end gap-2">
                <button
                  onClick={() => setConfirmAction(null)}
                  className="px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
                >
                  Annuler
                </button>
                <button
                  onClick={confirmAction === 'devoir' ? doReportDevoir : doReportExamen}
                  className="px-3 py-1.5 text-sm bg-blue-600 hover:bg-blue-700 text-white rounded"
                >
                  Confirmer
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// ─── Sous-composants ──────────────────────────────────────────────────────────

const StatCard: React.FC<{ label: string; value: string; icon: React.ReactNode; color: string }> = ({ label, value, icon }) => (
  <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-3">
    <div className="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400">
      {icon}
      {label}
    </div>
    <div className="text-xl font-bold text-gray-900 dark:text-gray-100 mt-0.5">{value}</div>
  </div>
);

const ProgressCard: React.FC<{ label: string; pct: number; count: string }> = ({ label, pct, count }) => {
  const color = pct >= 80 ? 'bg-green-500' : pct >= 50 ? 'bg-amber-500' : 'bg-red-500';
  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-3">
      <div className="flex items-center justify-between">
        <span className="text-xs text-gray-500 dark:text-gray-400">{label}</span>
        <span className="text-xs font-mono text-gray-600 dark:text-gray-300">{count}</span>
      </div>
      <div className="text-xl font-bold text-gray-900 dark:text-gray-100 mt-0.5">{pct}%</div>
      <div className="h-1.5 mt-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
        <div className={`h-full ${color} transition-all`} style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
};

const Hint: React.FC<{ level: 'ok' | 'warn'; text: string }> = ({ level, text }) => {
  const cls =
    level === 'warn'
      ? 'bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-700/50 text-amber-800 dark:text-amber-300'
      : 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-700/50 text-green-800 dark:text-green-300';
  const Icon = level === 'warn' ? AlertTriangle : CheckCircle2;
  return (
    <div className={`flex items-center gap-1.5 px-2 py-1.5 rounded border ${cls}`}>
      <Icon size={12} />
      <span>{text}</span>
    </div>
  );
};

const TabBtn: React.FC<{ active: boolean; onClick: () => void; icon: React.ReactNode; children: React.ReactNode }> = ({
  active, onClick, icon, children,
}) => (
  <button
    onClick={onClick}
    className={`inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
      active
        ? 'border-blue-600 text-blue-600 dark:border-blue-400 dark:text-blue-400'
        : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300'
    }`}
  >
    {icon}
    {children}
  </button>
);

const AnalyseView: React.FC<{ analyse: AnalyseCompletudeResponse }> = ({ analyse }) => (
  <div className="space-y-4 text-sm">
    <Section title={`Manque les deux devoirs (${analyse.manque_les_deux.length})`} students={analyse.manque_les_deux} />
    <Section title={`Manque uniquement D1 (${analyse.manque_d1.length})`} students={analyse.manque_d1} />
    <Section title={`Manque uniquement D2 (${analyse.manque_d2.length})`} students={analyse.manque_d2} />
    <Section title={`Manque la note d'examen (${analyse.manque_examen.length})`} students={analyse.manque_examen} />
  </div>
);

const Section: React.FC<{ title: string; students: EtudiantManquant[] }> = ({ title, students }) => {
  if (students.length === 0) return null;
  return (
    <div>
      <h4 className="text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wide mb-1.5">{title}</h4>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-1.5">
        {students.slice(0, 50).map((e) => (
          <div key={e.etudiant_id} className="text-xs px-2 py-1 rounded bg-gray-50 dark:bg-gray-700/30 border border-gray-200 dark:border-gray-700">
            <span className="font-mono text-gray-500 mr-1">{e.matricule}</span>
            <span className="text-gray-700 dark:text-gray-300">{e.nom} {e.prenoms}</span>
            <span className="text-gray-400 ml-1">— {e.classe}</span>
          </div>
        ))}
        {students.length > 50 && (
          <div className="text-xs text-gray-400 italic">… et {students.length - 50} de plus</div>
        )}
      </div>
    </div>
  );
};

const CandidatsList: React.FC<{
  etudiants: EtudiantManquant[];
  selection: Set<number>;
  onToggle: (id: number) => void;
  onToggleAll: () => void;
  tab: 'devoir_manquant' | 'examen_vers_devoir';
}> = ({ etudiants, selection, onToggle, onToggleAll, tab }) => {
  if (etudiants.length === 0) {
    return (
      <div className="text-center py-12 text-sm text-gray-500 dark:text-gray-400">
        <CheckCircle2 size={32} className="mx-auto text-gray-300 dark:text-gray-600 mb-2" />
        Aucun étudiant concerné par cette action.
      </div>
    );
  }

  const allSelected = selection.size === etudiants.length;

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-xs">
        <thead className="bg-gray-50 dark:bg-gray-700/30 sticky top-0">
          <tr>
            <th className="w-8 px-2 py-2">
              <input
                type="checkbox"
                checked={allSelected}
                onChange={onToggleAll}
                className="rounded border-gray-300 dark:border-gray-600"
              />
            </th>
            <th className="px-2 py-2 text-left font-semibold text-gray-600 dark:text-gray-300">Matricule</th>
            <th className="px-2 py-2 text-left font-semibold text-gray-600 dark:text-gray-300">Nom</th>
            <th className="px-2 py-2 text-left font-semibold text-gray-600 dark:text-gray-300">Classe</th>
            <th className="px-2 py-2 text-center font-semibold text-gray-600 dark:text-gray-300">D1</th>
            <th className="px-2 py-2 text-center font-semibold text-gray-600 dark:text-gray-300">D2</th>
            <th className="px-2 py-2 text-center font-semibold text-gray-600 dark:text-gray-300">Examen</th>
            <th className="px-2 py-2 text-center font-semibold text-gray-600 dark:text-gray-300">Action</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
          {etudiants.map((e) => {
            const selected = selection.has(e.etudiant_id);
            const action = computeAction(e, tab);
            return (
              <tr key={e.etudiant_id} className={selected ? '' : 'opacity-50'}>
                <td className="px-2 py-1.5">
                  <input
                    type="checkbox"
                    checked={selected}
                    onChange={() => onToggle(e.etudiant_id)}
                    className="rounded border-gray-300 dark:border-gray-600"
                  />
                </td>
                <td className="px-2 py-1.5 font-mono text-gray-600 dark:text-gray-300">{e.matricule}</td>
                <td className="px-2 py-1.5 text-gray-800 dark:text-gray-200">{e.nom} {e.prenoms}</td>
                <td className="px-2 py-1.5 text-gray-500 dark:text-gray-400">{e.classe}</td>
                <td className="px-2 py-1.5 text-center font-mono">{cellNote(e.note_d1, e.absent_d1)}</td>
                <td className="px-2 py-1.5 text-center font-mono">{cellNote(e.note_d2, e.absent_d2)}</td>
                <td className="px-2 py-1.5 text-center font-mono">{cellNote(e.note_examen, e.absent_examen)}</td>
                <td className="px-2 py-1.5 text-center text-blue-600 dark:text-blue-400 text-[11px]">{action}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

function cellNote(n: number | null, absent: boolean) {
  if (absent) return <span className="text-red-600 dark:text-red-400">AB</span>;
  if (n === null) return <span className="text-gray-400">—</span>;
  return n.toFixed(2);
}

function computeAction(e: EtudiantManquant, tab: 'devoir_manquant' | 'examen_vers_devoir'): string {
  if (tab === 'devoir_manquant') {
    if (e.note_d1 !== null && e.note_d2 === null) return `D2 ← ${e.note_d1.toFixed(2)}`;
    if (e.note_d2 !== null && e.note_d1 === null) return `D1 ← ${e.note_d2.toFixed(2)}`;
    return '—';
  }
  if (tab === 'examen_vers_devoir') {
    if (e.note_examen !== null) return `D1+D2 ← ${e.note_examen.toFixed(2)}`;
    if (e.absent_examen) return 'D1+D2 ← AB';
    return '—';
  }
  return '';
}

export default ReportIntelligentModal;
